<script setup>
import { ref, computed, watch } from "vue";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import { ChevronRight, ChevronDown, Check, X } from "lucide-vue-next";
import { cn } from "@/shared/lib/utils";

const props = defineProps({
  modelValue: { type: [Array, String, Number], default: undefined },
  options: { type: Array, default: () => [] },
  props: { type: Object, default: () => ({}) },
  placeholder: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  clearable: { type: Boolean, default: false },
  showAllLevels: { type: Boolean, default: true },
  emitPath: { type: Boolean, default: true },
  size: { type: String, default: "default" },
  class: { type: [String, Object, Array], default: undefined },
});

const emit = defineEmits(["update:modelValue", "change"]);

const open = ref(false);
const expandedParent = ref(null);

const sizeClass = computed(() => {
  const map = { lg: "h-10", sm: "h-8", default: "h-9" };
  return map[props.size] || "h-9";
});

const displayLabel = computed(() => {
  if (!props.modelValue || !props.modelValue.length) return "";
  const path = Array.isArray(props.modelValue)
    ? props.modelValue
    : [props.modelValue];

  const labels = [];
  let current = props.options;
  for (const val of path) {
    const found = current?.find((o) => o.value === val);
    if (found) {
      labels.push(found.label);
      current = found.children;
    }
  }

  return props.showAllLevels
    ? labels.join(" / ")
    : labels[labels.length - 1] || "";
});

function selectChild(parent, child) {
  const value = props.emitPath ? [parent.value, child.value] : child.value;
  emit("update:modelValue", value);
  emit("change", value);
  open.value = false;
  expandedParent.value = null;
}

function clear() {
  emit("update:modelValue", props.emitPath ? [] : "");
  emit("change", props.emitPath ? [] : "");
}

watch(open, (val) => {
  if (!val) expandedParent.value = null;
});
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <div
        :class="
          cn(
            'flex w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 cursor-pointer text-foreground',
            sizeClass,
            disabled && 'opacity-50 pointer-events-none',
            props.class
          )
        "
      >
        <span v-if="displayLabel" class="flex-1 text-sm truncate">
          {{ displayLabel }}
        </span>
        <span v-else class="flex-1 text-sm text-muted-foreground truncate">
          {{ placeholder }}
        </span>
        <button
          v-if="clearable && displayLabel"
          type="button"
          class="shrink-0 rounded-sm opacity-50 hover:opacity-100"
          @click.stop.prevent="clear"
        >
          <X class="size-3.5" />
        </button>
        <ChevronDown class="size-4 shrink-0 opacity-50" />
      </div>
    </PopoverTrigger>
    <PopoverContent
      class="w-[var(--reka-popover-trigger-width)] p-0"
      align="start"
    >
      <div class="flex">
        <!-- Level 1 -->
        <div class="flex-1 max-h-60 overflow-y-auto p-1 border-r border-border">
          <button
            v-for="opt in options"
            :key="opt.value"
            type="button"
            :disabled="opt.disabled"
            :class="[
              'flex w-full items-center justify-between rounded-sm px-2.5 py-2 text-sm text-left cursor-pointer outline-none hover:bg-accent hover:text-accent-foreground',
              expandedParent?.value === opt.value &&
                'bg-accent text-accent-foreground',
              opt.disabled && 'opacity-50 pointer-events-none',
            ]"
            @click="expandedParent = opt"
          >
            <span class="flex-1 truncate">{{ opt.label }}</span>
            <ChevronRight
              v-if="opt.children?.length"
              class="size-4 shrink-0 opacity-50"
            />
          </button>
        </div>
        <!-- Level 2 -->
        <div
          v-if="expandedParent?.children?.length"
          class="flex-1 max-h-60 overflow-y-auto p-1"
        >
          <button
            v-for="child in expandedParent.children"
            :key="child.value"
            type="button"
            :disabled="child.disabled"
            :class="[
              'flex w-full items-center justify-between rounded-sm px-2.5 py-2 text-sm text-left cursor-pointer outline-none hover:bg-accent hover:text-accent-foreground',
              child.disabled && 'opacity-50 pointer-events-none',
            ]"
            @click="selectChild(expandedParent, child)"
          >
            <span class="flex-1 truncate">{{ child.label }}</span>
            <Check
              v-if="
                Array.isArray(modelValue) && modelValue.includes(child.value)
              "
              class="size-4 shrink-0"
            />
          </button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
